import React, { forwardRef } from 'react';

// Common Label component for Admin forms
export interface AdminLabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  required?: boolean;
  optionalText?: string;
}

export const AdminLabel: React.FC<AdminLabelProps> = ({
  children,
  className = '',
  required,
  optionalText,
  ...props
}) => {
  return (
    <label
      className={`block text-xs font-bold text-slate-200 tracking-wide mb-1.5 ${className}`}
      {...props}
    >
      <span className="flex items-center justify-between">
        <span>
          {children}
          {required && <span className="text-rose-500 ml-1 font-black">*</span>}
        </span>
        {optionalText && (
          <span className="text-[11px] font-normal text-slate-400 ml-2">
            {optionalText}
          </span>
        )}
      </span>
    </label>
  );
};

// Admin Form Field Container (wraps label, control, helper, error)
export interface AdminFormFieldProps {
  label?: React.ReactNode;
  htmlFor?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  optionalText?: string;
  className?: string;
  children: React.ReactNode;
}

export const AdminFormField: React.FC<AdminFormFieldProps> = ({
  label,
  htmlFor,
  error,
  helperText,
  required,
  optionalText,
  className = '',
  children,
}) => {
  return (
    <div className={`space-y-1.5 ${className}`}>
      {label && (
        <AdminLabel htmlFor={htmlFor} required={required} optionalText={optionalText}>
          {label}
        </AdminLabel>
      )}
      {children}
      {error && (
        <p className="text-xs font-semibold text-rose-600 flex items-center gap-1 mt-1">
          <span>●</span>
          <span>{error}</span>
        </p>
      )}
      {!error && helperText && (
        <p className="text-[11px] text-slate-600 leading-normal mt-1">
          {helperText}
        </p>
      )}
    </div>
  );
};

// Standard Admin Input
export interface AdminInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  icon?: React.ReactNode;
  rightElement?: React.ReactNode;
}

export const AdminInput = forwardRef<HTMLInputElement, AdminInputProps>(
  ({ className = '', error, icon, rightElement, ...props }, ref) => {
    return (
      <div className="relative flex items-center w-full">
        {icon && (
          <div className="absolute left-3 flex items-center pointer-events-none text-slate-600">
            {icon}
          </div>
        )}
        <input
          ref={ref}
          className={`w-full bg-slate-900 text-slate-100 placeholder:text-slate-500 rounded-xl border text-xs sm:text-sm font-medium transition-all duration-150 outline-none
            ${icon ? 'pl-9' : 'pl-3.5'}
            ${rightElement ? 'pr-10' : 'pr-3.5'}
            py-2.5
            ${
              error
                ? 'border-rose-500 focus:border-rose-400 focus:ring-2 focus:ring-rose-500/20'
                : 'border-slate-800 hover:border-slate-700 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 shadow-xs'
            }
            disabled:bg-slate-950 disabled:text-slate-600 disabled:cursor-not-allowed
            ${className}`}
          {...props}
        />
        {rightElement && (
          <div className="absolute right-3 flex items-center text-slate-400">
            {rightElement}
          </div>
        )}
      </div>
    );
  }
);
AdminInput.displayName = 'AdminInput';

// Standard Admin Textarea
export interface AdminTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
}

export const AdminTextarea = forwardRef<HTMLTextAreaElement, AdminTextareaProps>(
  ({ className = '', error, rows = 3, ...props }, ref) => {
    return (
      <textarea
        ref={ref}
        rows={rows}
        className={`w-full bg-slate-900 text-slate-100 placeholder:text-slate-500 rounded-xl border text-xs sm:text-sm font-medium px-3.5 py-2.5 transition-all duration-150 outline-none
          ${
            error
              ? 'border-rose-500 focus:border-rose-400 focus:ring-2 focus:ring-rose-500/20'
              : 'border-slate-800 hover:border-slate-700 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 shadow-xs'
          }
          disabled:bg-slate-950 disabled:text-slate-600 disabled:cursor-not-allowed
          ${className}`}
        {...props}
      />
    );
  }
);
AdminTextarea.displayName = 'AdminTextarea';

// Standard Admin Select
export interface AdminSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  error?: boolean;
}

export const AdminSelect = forwardRef<HTMLSelectElement, AdminSelectProps>(
  ({ className = '', error, children, ...props }, ref) => {
    return (
      <div className="relative w-full">
        <select
          ref={ref}
          className={`w-full bg-slate-900 text-slate-100 rounded-xl border text-xs sm:text-sm font-medium px-3.5 py-2.5 appearance-none transition-all duration-150 outline-none pr-9
            ${
              error
                ? 'border-rose-500 focus:border-rose-400 focus:ring-2 focus:ring-rose-500/20'
                : 'border-slate-800 hover:border-slate-700 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 shadow-xs'
            }
            disabled:bg-slate-950 disabled:text-slate-600 disabled:cursor-not-allowed
            ${className}`}
          {...props}
        >
          {children}
        </select>
        <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
          <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
            <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
          </svg>
        </div>
      </div>
    );
  }
);
AdminSelect.displayName = 'AdminSelect';
